import React from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  USE_REAL_BILLING_API,
  saIsRealNotificationsMode,
  saIsRealTenantsMode,
  saShouldUseMockTenants,
} from '../../components/mvp-config.jsx';
import {
  formatBillingStatusError,
  formatOperationalStatusError,
  tenantService,
} from '../../components/services.jsx';
import { STORES } from '../../components/data.jsx';
import { useAppShell } from '../layout/ProtectedAppLayout.jsx';
import { paths } from '../router/app-paths.js';
import { RouteAvailabilityGuard } from '../router/guards/RouteAvailabilityGuard.jsx';
import { ModuleTabCanonicalRedirect } from '../router/ModuleTabCanonicalRedirect.jsx';
import { listModuleTabIds } from '../router/module-tabs.js';
import { matchStoreByTenantParam, resolveTenantIdForApi } from '../router/paths.js';
import { RouteChunkErrorBoundary } from './RouteChunkErrorBoundary.jsx';
import { ContentLoadingFallback } from './route-loading.jsx';

/**
 * Lazily-loaded heavy page components — one dynamic import() per module so
 * Vite can code-split them out of the main chunk. `RouteAvailabilityGuard`
 * (see `withAvailability` below) always runs BEFORE these are touched, so a
 * blocked route (support, integrations without flag, mock-only, …) never
 * triggers the chunk download.
 */
const AccessProfilesPage = React.lazy(() => import('../../components/access-profiles-page.jsx').then((m) => ({ default: m.AccessProfilesPage })));
const AuditoriaV2Page = React.lazy(() => import('../../components/auditoria-v2.jsx').then((m) => ({ default: m.AuditoriaV2Page })));
const BillingPage = React.lazy(() => import('../../components/billing-full.jsx').then((m) => ({ default: m.BillingPage })));
const Dashboard = React.lazy(() => import('../../components/dashboard.jsx').then((m) => ({ default: m.Dashboard })));
const EquipePermissoesPage = React.lazy(() => import('../../components/equipe-permissoes.jsx').then((m) => ({ default: m.EquipePermissoesPage })));
const InadimplenciaPage = React.lazy(() => import('../../components/inadimplencia.jsx').then((m) => ({ default: m.InadimplenciaPage })));
const IntegrationsPage = React.lazy(() => import('../../components/integrations.jsx').then((m) => ({ default: m.IntegrationsPage })));
const LojaDetail = React.lazy(() => import('../../components/loja-detail.jsx').then((m) => ({ default: m.LojaDetail })));
const LojasPage = React.lazy(() => import('../../components/lojas.jsx').then((m) => ({ default: m.LojasPage })));
const NewStorePage = React.lazy(() => import('../../components/new-store-page.jsx').then((m) => ({ default: m.NewStorePage })));
const NotificacoesOperacionaisPage = React.lazy(() => import('../../components/notificacoes-operacionais.jsx').then((m) => ({ default: m.NotificacoesOperacionaisPage })));
const ObservabilidadePage = React.lazy(() => import('../../components/observabilidade.jsx').then((m) => ({ default: m.ObservabilidadePage })));
const ConfigPage = React.lazy(() => import('../../components/other-pages.jsx').then((m) => ({ default: m.ConfigPage })));
const PlanosLegadoOutOfMvpPage = React.lazy(() => import('../../components/other-pages.jsx').then((m) => ({ default: m.PlanosLegadoOutOfMvpPage })));
const PlanosPage = React.lazy(() => import('../../components/other-pages.jsx').then((m) => ({ default: m.PlanosPage })));
const PlanosPermissoesPage = React.lazy(() => import('../../components/planos-permissoes.jsx').then((m) => ({ default: m.PlanosPermissoesPage })));
const RepairsPage = React.lazy(() => import('../../components/repairs.jsx').then((m) => ({ default: m.RepairsPage })));
const SupportKBPage = React.lazy(() => import('../../components/support-kb-page.jsx').then((m) => ({ default: m.SupportKBPage })));
const SupportReportsPage = React.lazy(() => import('../../components/support-reports-page.jsx').then((m) => ({ default: m.SupportReportsPage })));
const SupportTicketsPage = React.lazy(() => import('../../components/support-tickets-page.jsx').then((m) => ({ default: m.SupportTicketsPage })));
const TechSpecPage = React.lazy(() => import('../../components/tech-spec.jsx').then((m) => ({ default: m.TechSpecPage })));
const TenantCockpit = React.lazy(() => import('../../components/tenant-cockpit.jsx').then((m) => ({ default: m.TenantCockpit })));
const TenantCockpitMvp = React.lazy(() => import('../../components/tenant-cockpit.jsx').then((m) => ({ default: m.TenantCockpitMvp })));

/**
 * Wraps a lazily-loaded page component with its own error boundary + Suspense
 * fallback, so each page chunk fails/loads independently of the shell.
 */
function LazyPageBody({ Component, pageProps }) {
  // E2E-only hook — Playwright sets `window.__SA_E2E_FORCE_CHUNK_ERROR__` to simulate
  // a failed dynamic import without brittle network abort patterns in Vite dev mode.
  if (typeof window !== 'undefined' && window.__SA_E2E_FORCE_CHUNK_ERROR__) {
    throw new Error('E2E simulated chunk load failure');
  }
  return <Component {...pageProps} />;
}

function LazyPage({ component: Component, ...props }) {
  return (
    <RouteChunkErrorBoundary>
      <React.Suspense fallback={<ContentLoadingFallback />}>
        <LazyPageBody Component={Component} pageProps={props} />
      </React.Suspense>
    </RouteChunkErrorBoundary>
  );
}

function withAvailability(page, PageComponent) {
  return function GuardedPage(props) {
    const { showFuturePages } = useAppShell();
    return (
      <RouteAvailabilityGuard page={page} showFuturePages={showFuturePages}>
        <PageComponent {...props} />
      </RouteAvailabilityGuard>
    );
  };
}

function useTenantContextFromSearch() {
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get('tenantId');
  if (!tenantId) return null;
  return { id: tenantId, tenant_id: tenantId, name: tenantId };
}

export const DashboardRoute = withAvailability('dashboard', function DashboardRouteInner() {
  const { theme, stores, storesLoading, storesError, refetchStores } = useAppShell();
  return (
    <LazyPage
      component={Dashboard}
      theme={theme}
      stores={stores}
      storesLoading={storesLoading}
      storesError={storesError}
      onRetryStores={refetchStores}
    />
  );
});

export const LojasRoute = withAvailability('lojas', function LojasRouteInner() {
  const shell = useAppShell();
  const navigate = useNavigate();
  const { stores, storesListVersion, tweaks, setImpersonModal, setConfirmState, setOperationalModal, shellHandlers } = shell;
  return (
    <LazyPage
      component={LojasPage}
      onImpersonate={(store) => setImpersonModal(store)}
      onSuspend={(store) => shellHandlers?.handleSuspend?.(store, setConfirmState)}
      onOperationalAction={(store, action) => setOperationalModal({ store, action })}
      listVersion={storesListVersion}
      layout={tweaks.tableLayout}
      onNewStore={() => navigate(paths.novaLoja())}
      stores={stores}
    />
  );
});

export const NovaLojaRoute = withAvailability('nova-loja', function NovaLojaRouteInner() {
  const { addStore, logAction, showToast } = useAppShell();
  return (
    <LazyPage
      component={NewStorePage}
      onSave={(store) => {
        addStore(store);
        logAction({ acao: 'Criou tenant', loja: store.name, lojaId: store.id, kind: 'info' });
        if (store.send_invite_email === false) {
          showToast(`Tenant "${store.name}" criado (modo teste — sem e-mail)`);
        } else if (store.invite_email_sent === false) {
          showToast(`Tenant "${store.name}" criado, mas o e-mail de convite falhou`);
        } else {
          showToast(`Tenant "${store.name}" criado — convite enviado por e-mail`);
        }
        // Navigation to /lojas/:id is owned by NewStorePage via paths.loja.
      }}
    />
  );
});

/**
 * Load tenant by URL :tenantId. Optional location.state.store is cache only.
 */
function useTenantFromUrl() {
  const { tenantId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { stores, storesLoading, showToast } = useAppShell();
  const [loading, setLoading] = React.useState(true);
  const [store, setStore] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setNotFound(false);

      const fromState = location.state?.store;
      if (fromState && matchStoreByTenantParam([fromState], tenantId)) {
        if (!cancelled) {
          setStore(fromState);
          setLoading(false);
        }
        return;
      }

      const fromList = matchStoreByTenantParam(stores, tenantId);
      if (fromList) {
        if (!cancelled) {
          setStore(fromList);
          setLoading(false);
        }
        return;
      }

      if (storesLoading) return;

      if (typeof saShouldUseMockTenants === 'function' && saShouldUseMockTenants()) {
        const mock = matchStoreByTenantParam(STORES || [], tenantId);
        if (!cancelled) {
          if (mock) {
            setStore(mock);
          } else {
            setNotFound(true);
            showToast('Loja não encontrada');
            navigate(paths.lojas(), { replace: true });
          }
          setLoading(false);
        }
        return;
      }

      if (tenantService?.getById) {
        try {
          const tid = resolveTenantIdForApi({ id: tenantId, tenant_id: tenantId });
          const r = await tenantService.getById(tid);
          if (cancelled) return;
          if (r?.data) {
            setStore(r.data);
          } else {
            setNotFound(true);
            showToast('Loja salva não encontrada — redirecionado para Lojas');
            navigate(paths.lojas(), { replace: true });
          }
        } catch (e) {
          if (!cancelled) {
            setNotFound(true);
            showToast('Loja salva não encontrada — redirecionado para Lojas');
            navigate(paths.lojas(), { replace: true });
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setNotFound(true);
        showToast('Loja não encontrada');
        navigate(paths.lojas(), { replace: true });
        setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [tenantId, stores, storesLoading, location.state]);

  return { store, loading, notFound, tenantId };
}

export const LojaDetailRoute = withAvailability('loja', function LojaDetailRouteInner() {
  const shell = useAppShell();
  const { store, loading } = useTenantFromUrl();
  const {
    stores, updateStore, logAction, showToast,
    setImpersonModal, setPlanDrawerStore, setConfirmState, setOperationalModal, shellHandlers, setStoresListVersion, refetchStores,
  } = shell;

  if (loading || !store) {
    return <ContentLoadingFallback label="Carregando loja…" />;
  }

  const currentStore = stores.find((s) => s.id === store.id) || store;
  return (
    <LazyPage
      component={LojaDetail}
      store={currentStore}
      onImpersonate={(s) => setImpersonModal(s)}
      onSuspend={(s) => shellHandlers?.handleSuspend?.(s, setConfirmState)}
      onChangePlan={setPlanDrawerStore}
      onToast={showToast}
      onUpdate={(updates) => updateStore(updates?.id || currentStore.id, updates)}
      onStoresChanged={async () => {
        if (refetchStores) await refetchStores();
        setStoresListVersion((v) => v + 1);
      }}
      logAction={logAction}
    />
  );
});

export const CockpitRoute = withAvailability('cockpit', function CockpitRouteInner() {
  const shell = useAppShell();
  const { store, loading, tenantId } = useTenantFromUrl();
  const {
    stores, showToast, logAction,
    setImpersonModal, setPlanDrawerStore, setConfirmState, shellHandlers,
  } = shell;

  if (loading || !store) {
    return <ContentLoadingFallback label="Carregando loja…" />;
  }

  const currentStore = stores.find((s) => s.id === store.id) || store;
  const useCockpitMock = typeof saShouldUseMockTenants === 'function' && saShouldUseMockTenants();
  const body = useCockpitMock
    ? (
      <LazyPage
        component={TenantCockpit}
        store={currentStore}
        onImpersonate={(s) => setImpersonModal(s)}
        onSuspend={(s) => shellHandlers?.handleSuspend?.(s, setConfirmState)}
        onChangePlan={setPlanDrawerStore}
        onToast={showToast}
        logAction={logAction}
      />
    )
    : (
      <LazyPage
        component={TenantCockpitMvp}
        store={currentStore}
        onChangePlan={setPlanDrawerStore}
        onToast={showToast}
      />
    );
  return (
    <ModuleTabCanonicalRedirect moduleKey="cockpit" tenantId={tenantId}>
      {body}
    </ModuleTabCanonicalRedirect>
  );
});

export const PlanosRoute = withAvailability('planos', function PlanosRouteInner() {
  const { showToast, stores } = useAppShell();
  const usePlanosMock = typeof saShouldUseMockTenants === 'function' && saShouldUseMockTenants();
  return usePlanosMock
    ? <LazyPage component={PlanosPage} onToast={showToast} stores={stores} />
    : <LazyPage component={PlanosLegadoOutOfMvpPage} />;
});

export const PlanosPermissoesRoute = withAvailability('planos-permissoes', function PlanosPermissoesRouteInner() {
  const { showToast, stores, logAction } = useAppShell();
  const tenantContext = useTenantContextFromSearch();
  return (
    <LazyPage
      component={PlanosPermissoesPage}
      onToast={showToast}
      stores={stores}
      logAction={logAction}
      tenantContext={tenantContext}
    />
  );
});

export const InadimplenciaRoute = withAvailability('inadimplencia', function InadimplenciaRouteInner() {
  const { showToast, stores } = useAppShell();
  return (
    <ModuleTabCanonicalRedirect moduleKey="inadimplencia">
      <LazyPage component={InadimplenciaPage} onToast={showToast} stores={stores} />
    </ModuleTabCanonicalRedirect>
  );
});

export const BillingRoute = withAvailability('billing', function BillingRouteInner() {
  const { showToast, stores } = useAppShell();
  const tenantContext = useTenantContextFromSearch();
  return (
    <ModuleTabCanonicalRedirect moduleKey="billing">
      <LazyPage
        component={BillingPage}
        onToast={showToast}
        stores={stores}
        tenantContext={tenantContext}
      />
    </ModuleTabCanonicalRedirect>
  );
});

export const EquipeRoute = withAvailability('equipe', function EquipeRouteInner() {
  const { showToast, logAction } = useAppShell();
  return <LazyPage component={EquipePermissoesPage} onToast={showToast} logAction={logAction} />;
});

export const PerfisRoute = withAvailability('perfis', function PerfisRouteInner() {
  const { showToast, logAction, internalUsers } = useAppShell();
  return <LazyPage component={AccessProfilesPage} onToast={showToast} logAction={logAction} internalUsers={internalUsers} />;
});

export const ConfigRoute = withAvailability('config', function ConfigRouteInner() {
  const { showToast, logAction } = useAppShell();
  return <LazyPage component={ConfigPage} onToast={showToast} logAction={logAction} />;
});

export const AuditoriaRoute = withAvailability('auditoria', function AuditoriaRouteInner() {
  const { auditLog } = useAppShell();
  const tenantContext = useTenantContextFromSearch();
  return <LazyPage component={AuditoriaV2Page} auditLog={auditLog} tenantContext={tenantContext} />;
});

export const IntegrationsRoute = withAvailability('integrations', function IntegrationsRouteInner() {
  const { showToast, logAction } = useAppShell();
  const tenantContext = useTenantContextFromSearch();
  return (
    <ModuleTabCanonicalRedirect moduleKey="integrations">
      <LazyPage component={IntegrationsPage} onToast={showToast} logAction={logAction} tenantContext={tenantContext} />
    </ModuleTabCanonicalRedirect>
  );
});

export const ObservabilidadeRoute = withAvailability('observabilidade', function ObservabilidadeRouteInner() {
  const { showToast, logAction } = useAppShell();
  const tenantContext = useTenantContextFromSearch();
  return (
    <ModuleTabCanonicalRedirect moduleKey="observabilidade">
      <LazyPage component={ObservabilidadePage} onToast={showToast} logAction={logAction} tenantContext={tenantContext} />
    </ModuleTabCanonicalRedirect>
  );
});

export const RepairsRoute = withAvailability('repairs', function RepairsRouteInner() {
  const { showToast, logAction } = useAppShell();
  const location = useLocation();
  const tenantContext = useTenantContextFromSearch();
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get('ticketId');
  // Optional rich ticket cache from in-session navigation (support modules / blocked).
  const contextTicket = location.state?.contextTicket
    || (ticketId ? { id: ticketId } : null);
  return (
    <ModuleTabCanonicalRedirect moduleKey="repairs">
      <LazyPage
        component={RepairsPage}
        onToast={showToast}
        logAction={logAction}
        contextTicket={contextTicket}
        tenantContext={tenantContext}
      />
    </ModuleTabCanonicalRedirect>
  );
});

export const TicketsRoute = withAvailability('tickets', function TicketsRouteInner() {
  const { showToast, stores } = useAppShell();
  const [searchParams] = useSearchParams();
  const initialTicketId = searchParams.get('ticketId') || null;
  return (
    <ModuleTabCanonicalRedirect moduleKey="tickets">
      <LazyPage
        component={SupportTicketsPage}
        onToast={showToast}
        stores={stores}
        initialTicketId={initialTicketId}
      />
    </ModuleTabCanonicalRedirect>
  );
});

export const KnowledgeBaseRoute = withAvailability('knowledge-base', function KnowledgeBaseRouteInner() {
  const { showToast } = useAppShell();
  const location = useLocation();
  // articleContext optional cache; page also seeds from ?ticketId=.
  const articleContext = location.state?.articleContext || null;
  return (
    <ModuleTabCanonicalRedirect moduleKey="knowledge-base">
      <LazyPage
        component={SupportKBPage}
        onToast={showToast}
        articleContext={articleContext}
      />
    </ModuleTabCanonicalRedirect>
  );
});

export const SupportReportsRoute = withAvailability('support-reports', function SupportReportsRouteInner() {
  const { showToast } = useAppShell();
  return (
    <ModuleTabCanonicalRedirect moduleKey="support-reports">
      <LazyPage component={SupportReportsPage} onToast={showToast} />
    </ModuleTabCanonicalRedirect>
  );
});

export const NotificacoesOpsRoute = withAvailability('notificacoes-ops', function NotificacoesOpsRouteInner() {
  const { showToast } = useAppShell();
  const useRealNotif = typeof saIsRealNotificationsMode === 'function' && saIsRealNotificationsMode();
  const allowedNotifTabIds = listModuleTabIds('notificacoes-ops', { includeMockOnly: !useRealNotif });
  return (
    <ModuleTabCanonicalRedirect moduleKey="notificacoes-ops" allowedTabIds={allowedNotifTabIds}>
      <LazyPage component={NotificacoesOperacionaisPage} onToast={showToast} />
    </ModuleTabCanonicalRedirect>
  );
});

export const TechSpecRoute = withAvailability('tech-spec', function TechSpecRouteInner() {
  const { showToast } = useAppShell();
  return <LazyPage component={TechSpecPage} onToast={showToast} />;
});

export { USE_REAL_BILLING_API, formatBillingStatusError, formatOperationalStatusError, resolveTenantIdForApi, saIsRealTenantsMode };

/** Exposed for lazy-routes.test.js (pure inspection only — never rendered by tests). */
export const __LAZY_PAGE_FACTORIES__ = {
  AccessProfilesPage,
  AuditoriaV2Page,
  BillingPage,
  Dashboard,
  EquipePermissoesPage,
  InadimplenciaPage,
  IntegrationsPage,
  LojaDetail,
  LojasPage,
  NewStorePage,
  NotificacoesOperacionaisPage,
  ObservabilidadePage,
  ConfigPage,
  PlanosLegadoOutOfMvpPage,
  PlanosPage,
  PlanosPermissoesPage,
  RepairsPage,
  SupportKBPage,
  SupportReportsPage,
  SupportTicketsPage,
  TechSpecPage,
  TenantCockpit,
  TenantCockpitMvp,
};
